import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaCode, 
  FaMobile, 
  FaGlobe, 
  FaRocket, 
  FaCloud, 
  FaShieldAlt,
  FaCog,
  FaChartLine,
  FaUsers,
  FaPalette
} from 'react-icons/fa';
import './Services.css';

const Services = () => {
  const services = [
    {
      icon: <FaCode />,
      title: "Web Applications",
      description: "Custom web applications built with modern frameworks and cutting-edge technologies for optimal performance and user experience.",
      features: ["React & Next.js", "Node.js Backend", "Real-time Features", "Scalable Architecture"]
    },
    {
      icon: <FaMobile />,
      title: "Mobile Applications",
      description: "Native and cross-platform mobile apps that deliver exceptional user experiences across iOS and Android devices.",
      features: ["React Native", "Flutter", "iOS & Android", "App Store Optimization"]
    },
    {
      icon: <FaGlobe />,
      title: "Websites",
      description: "Stunning, responsive websites that combine beautiful design with powerful functionality and SEO optimization.",
      features: ["Responsive Design", "SEO Optimized", "Fast Loading", "Modern UI/UX"]
    },
    {
      icon: <FaRocket />,
      title: "Tech Platforms",
      description: "Innovative technology platforms that solve complex business problems and drive digital transformation.",
      features: ["Cloud Infrastructure", "API Development", "Data Analytics", "AI Integration"]
    }
  ];

  const additionalServices = [
    { icon: <FaCloud />, title: "Cloud Solutions" },
    { icon: <FaShieldAlt />, title: "Security Services" },
    { icon: <FaCog />, title: "DevOps & Automation" },
    { icon: <FaChartLine />, title: "Data Analytics" },
    { icon: <FaUsers />, title: "Consulting" },
    { icon: <FaPalette />, title: "UI/UX Design" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="services" className="services">
      <div className="container">
        <motion.div
          className="services-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <span className="section-badge">Our Services</span>
          <h2 className="section-title">
            Comprehensive <span className="text-gradient">Tech Solutions</span>
          </h2>
          <p className="section-description">
            From concept to deployment, we provide end-to-end technology solutions 
            that drive business growth and innovation.
          </p>
        </motion.div>

        <motion.div
          className="services-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="service-card"
              variants={itemVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
            >
              <div className="service-icon">
                {service.icon}
              </div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>
              <ul className="service-features">
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex}>{feature}</li>
                ))}
              </ul>
              <motion.button 
                className="service-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  const contactSection = document.querySelector('#contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                Learn More
              </motion.button>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="additional-services"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <h3 className="additional-title">Additional Services</h3>
          <div className="additional-grid">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                className="additional-item"
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="additional-icon">{service.icon}</div>
                <span>{service.title}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="services-cta"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          viewport={{ once: true }}
        >
          <div className="cta-content">
            <h3>Ready to Transform Your Digital Presence?</h3>
            <p>Let's discuss your project and bring your vision to life with cutting-edge technology.</p>
            <motion.a 
              href="#contact"
              className="cta-btn"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Your Project Today
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
